import { Suspense, lazy, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { can } from '@seller/shared';
import {
  api,
  ApiError,
  type ChildAsinMix,
  type FamilyChild,
  type FamilyDetailResult,
  type PlanRow,
  type PurchasePlanResult,
} from '../api/client.js';
import { useSession } from '../auth/useSession.js';
import { DataTable } from '../components/DataTable.js';
import { ProductThumb } from '../components/ProductThumb.js';
import { AnalysisBanner } from '../components/AnalysisBanner.js';
import {
  Alert,
  Button,
  Card,
  Input,
  PageHeader,
  Spinner,
  StockNote,
  TableScroll,
} from '../components/ui.js';
import { day, decimal, integer } from '../format.js';
import { KEY_SOURCE_LABEL } from '../lib/families.js';
import { CONFIDENCE_CLASS, CONFIDENCE_LABEL } from '../lib/purchase-plan.js';
import { parseCount } from '../lib/parse-input.js';
import {
  compareInteger,
  compareIsoDate,
  compareNumericString,
  compareText,
  sortingFnFor,
} from '../lib/table-sort.js';

import { WhenVisible } from '../components/WhenVisible.js';

const EmbeddedSalesStock = lazy(() => import('../charts/EmbeddedSalesStock.js'));

/**
 * One product family: the rolled-up header, its child SKUs sorted by variant,
 * and the size mix (each child's share of the family's units and stock). The
 * variant labels are a heuristic over the child titles — see docs/analysis.md,
 * "Product families".
 */

const columnHelper = createColumnHelper<FamilyChild>();

function abbr(label: string, full: string): ReactNode {
  return <span title={full}>{label}</span>;
}

const BASIS_LABEL: Record<FamilyChild['scoreBasis'], string> = {
  in_stock_velocity: 'In-stock',
  active_months: 'Monthly',
};

/** A share percentage with a plain inline bar (no chart library). */
function MixBar({ percent }: { percent: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="h-2 w-16 overflow-hidden rounded bg-sunken" aria-hidden>
        <div className="h-full rounded bg-brand-600" style={{ width: `${percent}%` }} />
      </div>
      <span className="tabular text-xs text-body">{decimal(percent, 1)}%</span>
    </div>
  );
}

const COLUMNS = [
  columnHelper.accessor('variantSortKey', {
    id: 'variant',
    header: () => abbr('Variant', 'Grip or shoe size, parsed from the title (a heuristic)'),
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => (
      <span className="font-medium text-strong">{c.row.original.variantLabel || '—'}</span>
    ),
  }),
  columnHelper.accessor('sellerSku', {
    header: 'SKU',
    sortingFn: sortingFnFor(compareText),
    cell: (c) => (
      <span className="inline-flex items-center gap-1.5">
        <ProductThumb asin={c.row.original.asin} title={c.row.original.title} />
        <Link
          to={`/skus/${c.row.original.id}`}
          className="whitespace-nowrap text-brand-600 hover:underline"
        >
          {c.getValue()}
        </Link>
        {c.row.original.duplicateHidden && (
          <span
            className="rounded bg-warn-bg px-1 py-0.5 text-[10px] font-medium text-warn-text ring-1 ring-inset ring-warn-line"
            title="Zero-stock duplicate on a shared child ASIN (hidden by default)"
          >
            dup
          </span>
        )}
      </span>
    ),
  }),
  columnHelper.accessor((r) => r.asin ?? undefined, {
    id: 'asin',
    header: 'ASIN',
    sortingFn: sortingFnFor(compareText),
    cell: (c) => c.getValue() ?? '—',
  }),
  columnHelper.accessor((r) => r.status ?? undefined, {
    id: 'status',
    header: 'Status',
    sortingFn: sortingFnFor(compareText),
    cell: (c) => c.getValue() ?? '—',
  }),
  columnHelper.accessor((r) => r.quantity ?? undefined, {
    id: 'quantity',
    header: () => abbr('Qty', 'On hand'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue() ?? null),
  }),
  columnHelper.accessor('units30', {
    header: () => abbr('U30', 'Units sold, last 30 days'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  columnHelper.accessor('units90', {
    header: () => abbr('U90', 'Units sold, last 90 days'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  columnHelper.accessor('units365', {
    header: () => abbr('U365', 'Units sold, last 365 days'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  columnHelper.accessor('unitsLifetime', {
    header: () => abbr('Life', 'Units sold, lifetime'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  columnHelper.accessor((r) => r.unitsPer30InStock ?? undefined, {
    id: 'unitsPer30InStock',
    header: () => abbr('U/30 IS', 'Units per 30 in-stock days, last stocked window'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareNumericString),
    cell: (c) => decimal(c.getValue() ?? null, 2),
  }),
  columnHelper.accessor((r) => r.sellThroughLastStocked ?? undefined, {
    id: 'sellThroughLastStocked',
    header: () => abbr('ST (LS)', 'Sell-through of the last stocked window'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareNumericString),
    cell: (c) => decimal(c.getValue() ?? null, 2),
  }),
  columnHelper.accessor((r) => r.lastSaleDate ?? undefined, {
    id: 'lastSaleDate',
    header: () => abbr('Last sale', 'Date of the last sale'),
    sortingFn: sortingFnFor(compareIsoDate),
    cell: (c) => day(c.getValue()),
  }),
  columnHelper.accessor('score', {
    header: () => abbr('Score', 'Restock score; higher is a stronger candidate'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareNumericString),
    cell: (c) => <span className="font-medium text-strong">{decimal(c.getValue(), 2)}</span>,
  }),
  columnHelper.accessor('scoreBasis', {
    header: 'Basis',
    sortingFn: sortingFnFor(compareText),
    cell: (c) => BASIS_LABEL[c.getValue()],
  }),
  columnHelper.accessor((r) => Number(r.share365), {
    id: 'share365',
    header: () => abbr('Mix 365', 'Share of the family units over the last 365 days'),
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => <MixBar percent={c.row.original.share365} />,
  }),
  columnHelper.accessor((r) => Number(r.shareLifetime), {
    id: 'shareLifetime',
    header: () => abbr('Mix life', 'Share of the family units over all loaded history'),
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => <MixBar percent={c.row.original.shareLifetime} />,
  }),
  columnHelper.accessor((r) => Number(r.shareStock), {
    id: 'shareStock',
    header: () => abbr('Mix stk', 'Share of the current on-hand stock'),
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => <MixBar percent={c.row.original.shareStock} />,
  }),
];

export function FamilyDetailPage() {
  const [params] = useSearchParams();
  const key = params.get('key') ?? '';
  // Hide zero-stock duplicates on a shared child ASIN (default on). The children
  // list, its size mix, and the per-ASIN roll-up are fetched at this flag, so the
  // server re-mixes over the shown set (shares sum to 100.0) and the CSV agrees.
  // The bulk action counts the full SKU set from the plan card's own query.
  const [hideDuplicates, setHideDuplicates] = useState(true);

  const query = useQuery({
    queryKey: ['family', key, hideDuplicates],
    queryFn: () =>
      api.get<FamilyDetailResult>(
        `/api/family?key=${encodeURIComponent(key)}&hideDuplicates=${hideDuplicates}`,
      ),
    enabled: key !== '',
  });

  if (query.isPending) return <Spinner label="Loading family" />;
  if (query.isError) return <Alert>{query.error.message}</Alert>;

  const { family, children, byChildAsin, hiddenDuplicates, stock } = query.data;
  const showAsinRollup = byChildAsin.some((g) => g.skuCount > 1);
  const csvHref = `/api/family.csv?key=${encodeURIComponent(family.key)}&hideDuplicates=${hideDuplicates}`;

  return (
    <div className="space-y-4">
      <div className="text-xs text-muted">
        <Link to="/families" className="hover:underline">
          Families
        </Link>{' '}
        / {family.name || family.key}
      </div>
      <PageHeader title={family.name || family.key} />

      <AnalysisBanner />

      <Card className="p-5">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
          <Item label="Key" value={family.key} />
          <Item label="Key source" value={KEY_SOURCE_LABEL[family.keySource]} />
          <Item
            label="Children"
            value={`${integer(family.childCount)} (${integer(family.activeChildren)} active)`}
          />
          <Item label="On hand" value={integer(family.stock)} />
        </dl>
        <div className="mt-3 border-t border-line pt-2 text-xs">
          <StockNote source={stock.source} asOf={stock.asOf} />
        </div>
      </Card>

      <FamilyPlanCard familyKey={family.key} hideDuplicates={hideDuplicates} />

      <Card className="p-4">
        <WhenVisible>
          <Suspense fallback={<Spinner label="Loading chart" />}>
            <EmbeddedSalesStock scope="family" chartKey={family.key} />
          </Suspense>
        </WhenVisible>
      </Card>

      <Card>
        <DataTable
          data={children}
          columns={COLUMNS}
          initialSorting={[{ id: 'variant', desc: false }]}
          storageKey="family-children"
          resetKey={String(hideDuplicates)}
          dense
          stickyFirstColumn
          emptyText="No child SKUs."
          toolbar={
            <>
              <span className="text-xs text-muted">
                {integer(children.length)} child SKU{children.length === 1 ? '' : 's'}
              </span>
              <span className="text-xs text-muted">
                Variant sizes are parsed from titles (a heuristic).
              </span>
              <label
                className="flex items-center gap-1.5 text-xs text-body"
                title="Among SKUs on one child ASIN, hide the zero-stock ones when a sibling has on-hand, inbound, or reserved stock."
              >
                <input
                  type="checkbox"
                  checked={hideDuplicates}
                  onChange={(e) => setHideDuplicates(e.target.checked)}
                  className="rounded border-line-strong"
                />
                Hide zero-stock duplicates
                {hiddenDuplicates > 0 && (
                  <span className="text-warn-text">
                    ({integer(hiddenDuplicates)} duplicate{hiddenDuplicates === 1 ? '' : 's'})
                  </span>
                )}
              </label>
              <a
                href={csvHref}
                className="inline-flex items-center rounded-md bg-surface px-3 py-2 text-sm font-medium text-body ring-1 ring-inset ring-line-strong hover:bg-sunken"
              >
                Download CSV
              </a>
            </>
          }
        />
      </Card>

      {showAsinRollup && <ChildAsinCard rows={byChildAsin} />}
    </div>
  );
}

/** The mix rolled up per child ASIN, for families where SKUs share an ASIN. */
function ChildAsinCard({ rows }: { rows: ChildAsinMix[] }) {
  return (
    <Card>
      <div className="border-b border-line px-4 py-2">
        <h2 className="text-sm font-medium text-body">By child ASIN</h2>
      </div>
      <TableScroll>
        <table className="w-full text-sm">
          <thead className="border-b border-line text-left text-xs text-muted">
            <tr>
              <th className="px-3 py-2 font-medium">ASIN</th>
              <th className="px-3 py-2 text-right font-medium">SKUs</th>
              <th className="px-3 py-2 text-right font-medium">U365</th>
              <th className="px-3 py-2 text-right font-medium">Life</th>
              <th className="px-3 py-2 text-right font-medium">Qty</th>
              <th className="px-3 py-2 font-medium">Mix 365</th>
              <th className="px-3 py-2 font-medium">Mix life</th>
              <th className="px-3 py-2 font-medium">Mix stk</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {rows.map((g) => (
              <tr key={g.asin}>
                <td className="whitespace-nowrap px-3 py-1.5 text-body">{g.asin}</td>
                <td className="tabular px-3 py-1.5 text-right">{integer(g.skuCount)}</td>
                <td className="tabular px-3 py-1.5 text-right">{integer(g.units365)}</td>
                <td className="tabular px-3 py-1.5 text-right">{integer(g.unitsLifetime)}</td>
                <td className="tabular px-3 py-1.5 text-right">{integer(g.stock)}</td>
                <td className="px-3 py-1.5">
                  <MixBar percent={g.share365} />
                </td>
                <td className="px-3 py-1.5">
                  <MixBar percent={g.shareLifetime} />
                </td>
                <td className="px-3 py-1.5">
                  <MixBar percent={g.shareStock} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </Card>
  );
}

function Item({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="text-body">{value}</dd>
    </div>
  );
}

/**
 * The family's purchase plan: each size's suggested and final quantities, the
 * family totals, and — for an importer — a bulk action that sets the case pack,
 * lead-time override, or do-not-reorder flag on every size at once.
 *
 * The plan is fetched with the duplicates NOT hidden, so the bulk action can
 * count and preview every SKU it will write (it writes all SKUs under the family
 * key, hidden stragglers included — marking a stale duplicate do-not-reorder is
 * the point). The mini-table follows the page's own hide toggle, so the two
 * tables on the page agree, and its Total is over the same displayed set.
 */
function FamilyPlanCard({
  familyKey,
  hideDuplicates,
}: {
  familyKey: string;
  hideDuplicates: boolean;
}) {
  const session = useSession();
  const canEdit = session.status === 'signedIn' && can(session.user.role, 'data:import');
  const queryClient = useQueryClient();

  const plan = useQuery({
    queryKey: ['purchase-plan', 'sku', false],
    queryFn: () =>
      api.get<PurchasePlanResult<PlanRow>>('/api/purchase-plan?scope=sku&hideDuplicates=false'),
  });
  const allRows = (plan.data?.rows ?? [])
    .filter((r) => r.family?.key === familyKey)
    .sort((a, b) => a.variantSortKey - b.variantSortKey || a.sellerSku.localeCompare(b.sellerSku));
  // Display follows the page toggle; the bulk action keeps every SKU it writes.
  const rows = hideDuplicates ? allRows.filter((r) => !r.duplicateHidden) : allRows;
  const planReady = !plan.isPending && !plan.isError;

  const totalSuggested = rows.reduce((sum, r) => sum + r.suggestedQty, 0);
  const totalFinal = rows.reduce((sum, r) => sum + r.finalQty, 0);
  const currentPlan = plan.data?.plan ?? null;

  return (
    <Card>
      <div className="flex items-center justify-between border-b border-line px-4 py-2">
        <h2 className="text-sm font-medium text-body">
          Purchase plan
          {currentPlan && (
            <span className="ml-2 text-xs font-normal text-muted">
              {currentPlan.name}
              {currentPlan.status === 'sent' ? ' (sent — read-only)' : ''}
            </span>
          )}
        </h2>
        <Link to="/purchase-plan" className="text-xs text-brand-600 hover:underline">
          Open the plan
        </Link>
      </div>
      {plan.isPending ? (
        <p className="p-4 text-sm text-muted">Loading…</p>
      ) : plan.isError ? (
        <div className="p-4">
          <Alert>Could not load the plan: {plan.error.message}</Alert>
        </div>
      ) : rows.length === 0 ? (
        <p className="p-4 text-sm text-muted">No plan rows for this family yet.</p>
      ) : (
        <TableScroll>
          <table className="w-full text-sm">
            <thead className="border-b border-line text-left text-xs text-muted">
              <tr>
                <th className="px-3 py-2 font-medium">Variant</th>
                <th className="px-3 py-2 font-medium">SKU</th>
                <th className="px-3 py-2 text-right font-medium">On hand + inbound</th>
                <th className="px-3 py-2 text-right font-medium">Suggested</th>
                <th className="px-3 py-2 text-right font-medium">Final</th>
                <th className="px-3 py-2 font-medium">Order by</th>
                <th className="px-3 py-2 font-medium">Confidence</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="whitespace-nowrap px-3 py-1.5 font-medium text-strong">
                    {r.variantLabel || '—'}
                  </td>
                  <td className="px-3 py-1.5">
                    <Link to={`/skus/${r.id}`} className="text-brand-600 hover:underline">
                      {r.sellerSku}
                    </Link>
                  </td>
                  <td className="tabular px-3 py-1.5 text-right">{integer(r.position)}</td>
                  <td className="tabular px-3 py-1.5 text-right">{integer(r.suggestedQty)}</td>
                  <td className="tabular px-3 py-1.5 text-right font-medium text-strong">
                    {integer(r.finalQty)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    <span className={r.late ? 'font-medium text-danger-text' : ''}>
                      {day(r.orderByDate)}
                    </span>
                  </td>
                  <td className="px-3 py-1.5">
                    <span
                      className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset ${CONFIDENCE_CLASS[r.confidence]}`}
                      title={r.reasons.join('\n')}
                    >
                      {CONFIDENCE_LABEL[r.confidence]}
                    </span>
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-line-strong font-medium text-strong">
                <td className="px-3 py-1.5" colSpan={3}>
                  Total
                </td>
                <td className="tabular px-3 py-1.5 text-right">{integer(totalSuggested)}</td>
                <td className="tabular px-3 py-1.5 text-right">{integer(totalFinal)}</td>
                <td className="px-3 py-1.5" colSpan={2} />
              </tr>
            </tbody>
          </table>
        </TableScroll>
      )}
      {canEdit && (
        <BulkPlanningForm
          familyKey={familyKey}
          // ALL SKUs under the key — the write touches every one, so the preview
          // "N sizes" and "(N differ)" must be over the full list, hidden included.
          rows={allRows}
          count={allRows.length}
          planReady={planReady}
          onSaved={() =>
            Promise.all([
              queryClient.invalidateQueries({ queryKey: ['purchase-plan'] }),
              queryClient.invalidateQueries({ queryKey: ['sku'] }),
              queryClient.invalidateQueries({ queryKey: ['family'] }),
              queryClient.invalidateQueries({ queryKey: ['families'] }),
              queryClient.invalidateQueries({ queryKey: ['restock'] }),
            ])
          }
        />
      )}
    </Card>
  );
}

/**
 * The importer's bulk action: set or clear a case pack or lead-time override, or
 * flip do-not-reorder, across every size at once. Input is parsed strictly, and
 * nothing is written until the owner confirms a plain-words preview of exactly
 * what will change ("Set case pack to 6 on 5 sizes (2 currently differ)").
 */
interface Pending {
  body: Record<string, unknown>;
  lines: string[];
}

function BulkPlanningForm({
  familyKey,
  rows,
  count,
  planReady,
  onSaved,
}: {
  familyKey: string;
  rows: PlanRow[];
  /** The family's size count — every SKU under the key, from the plan rows (all
   * of them), so the preview count matches the rows the write touches. */
  count: number;
  /** The plan query has settled successfully; only then can a change be confirmed. */
  planReady: boolean;
  onSaved: () => Promise<unknown>;
}) {
  const [casePack, setCasePack] = useState('');
  const [leadTime, setLeadTime] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);

  const post = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<{ updated: number }>(
        `/api/family/planning?key=${encodeURIComponent(familyKey)}`,
        body,
      ),
    onSuccess: async (result) => {
      setError(null);
      setPending(null);
      setCasePack('');
      setLeadTime('');
      setSaved(`Updated ${result.updated} size${result.updated === 1 ? '' : 's'}.`);
      await onSaved();
    },
    onError: (caught: unknown) => {
      setSaved(null);
      setPending(null);
      setError(caught instanceof ApiError ? caught.message : 'Could not apply the change.');
    },
  });

  const sizes = (n: number) => `${n} size${n === 1 ? '' : 's'}`;

  /** Build the field-set preview from the case-pack and lead-time inputs. */
  function previewSet() {
    setSaved(null);
    const body: Record<string, unknown> = {};
    const lines: string[] = [];
    if (casePack.trim() !== '') {
      const p = parseCount(casePack, { min: 1 });
      if (p.kind !== 'value') {
        setError(`Case pack: ${p.kind === 'error' ? p.message : 'enter a value.'}`);
        return;
      }
      body.casePack = p.value;
      const differ = rows.filter((r) => r.casePack !== p.value).length;
      lines.push(`Set case pack to ${p.value} on ${sizes(count)} (${differ} currently differ).`);
    }
    if (leadTime.trim() !== '') {
      const p = parseCount(leadTime, { min: 0 });
      if (p.kind !== 'value') {
        setError(`Lead time: ${p.kind === 'error' ? p.message : 'enter a value.'}`);
        return;
      }
      body.leadTimeDaysOverride = p.value;
      const differ = rows.filter((r) => r.leadTimeDaysOverride !== p.value).length;
      lines.push(
        `Set lead-time override to ${p.value} on ${sizes(count)} (${differ} currently differ).`,
      );
    }
    if (Object.keys(body).length === 0) {
      setError('Enter a case pack or lead time to apply to every size.');
      return;
    }
    setError(null);
    setPending({ body, lines });
  }

  function previewClear(field: 'casePack' | 'leadTimeDaysOverride', label: string) {
    setSaved(null);
    setError(null);
    setPending({ body: { [field]: null }, lines: [`Clear the ${label} on ${sizes(count)}.`] });
  }

  function previewExclude(next: boolean) {
    setSaved(null);
    setError(null);
    setPending({
      body: { reorderExcluded: next },
      lines: [
        `${next ? 'Mark' : 'Allow reorder for'} all ${sizes(count)}${next ? ' do-not-reorder' : ''}.`,
      ],
    });
  }

  return (
    <div className="border-t border-line px-4 py-3 text-xs">
      {pending ? (
        <div className="space-y-2">
          <p className="font-medium text-body">Confirm bulk change:</p>
          <ul className="list-disc pl-5 text-body">
            {pending.lines.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              disabled={post.isPending || !planReady}
              onClick={() => post.mutate(pending.body)}
            >
              {post.isPending ? 'Applying…' : 'Confirm'}
            </Button>
            <Button variant="ghost" disabled={post.isPending} onClick={() => setPending(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <span className="text-muted">Apply to every size:</span>
          <label className="block">
            <span className="mb-1 block text-muted">Case pack</span>
            <Input
              value={casePack}
              onChange={(e) => setCasePack(e.target.value)}
              className="w-24"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-muted">Lead time (days)</span>
            <Input
              value={leadTime}
              onChange={(e) => setLeadTime(e.target.value)}
              className="w-28"
            />
          </label>
          <Button type="button" variant="secondary" disabled={!planReady} onClick={previewSet}>
            Preview
          </Button>
          <Button
            type="button"
            variant="ghost"
            disabled={!planReady}
            onClick={() => previewClear('casePack', 'case pack')}
          >
            Clear case pack
          </Button>
          <Button
            type="button"
            variant="ghost"
            disabled={!planReady}
            onClick={() => previewClear('leadTimeDaysOverride', 'lead-time override')}
          >
            Clear lead time
          </Button>
          <Button
            type="button"
            variant="ghost"
            disabled={!planReady}
            onClick={() => previewExclude(true)}
          >
            Do not reorder (all sizes)
          </Button>
          <Button
            type="button"
            variant="ghost"
            disabled={!planReady}
            onClick={() => previewExclude(false)}
          >
            Allow reorder (all sizes)
          </Button>
          {!planReady && <span className="text-muted">Waiting for the plan to load…</span>}
          {saved && <span className="text-ok-text">{saved}</span>}
          {error && <span className="text-danger-text">{error}</span>}
        </div>
      )}
    </div>
  );
}
